import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { SearchInput } from "@/components/common/SearchInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { relative } from "@/lib/format";
import { History } from "lucide-react";

const ACTION_COLOR = { status_change: "blue", reassign: "violet", delete: "red", update: "amber", employment_change: "violet", create: "green", submit_approval: "amber", approve: "green", reject: "red" };
const ENTITY_LABEL = {
  work_item: "Ticket", project: "Project", employee: "Employee", leave_application: "Leave Application",
  reimbursement: "Reimbursement", dms_file: "Document", asset: "Asset", payroll: "Payroll",
  checklist: "Checklist", perf_review: "Performance Review", work_item_approval: "Approval",
  work_item_approval_config: "Approval Config",
  user: "User Account", session: "Session", master_password: "Master Password",
};

export default function AuditLog() {
  const [entityType, setEntityType] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [search, setSearch] = useState("");
  const { data: entries, isLoading } = useQuery({ queryKey: ["audit-log"], queryFn: () => get("/audit-log") });

  const actions = useMemo(() => [...new Set((entries || []).map((e) => e.action).filter(Boolean))], [entries]);
  const filtered = useMemo(() => {
    if (!entries) return [];
    return entries.filter((e) => {
      if (entityType !== "all" && e.entity_type !== entityType) return false;
      if (actionFilter !== "all" && e.action !== actionFilter) return false;
      if (search && !`${e.user_name} ${e.entity_id}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [entries, entityType, actionFilter, search]);

  if (isLoading) return <Skeleton className="h-64" />;

  return (
    <div>
      <PageHeader title="Audit Log" subtitle="Every tracked change across the app, who made it, and when.">
        <SearchInput testid="audit-search" placeholder="Search actor or entity id" value={search} onChange={setSearch} />
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-40" data-testid="audit-action-filter"><SelectValue placeholder="All actions" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            {actions.map((a) => <SelectItem key={a} value={a} className="capitalize">{a.replace("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={entityType} onValueChange={setEntityType}>
          <SelectTrigger className="w-44" data-testid="audit-entity-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All entities</SelectItem>
            {Object.entries(ENTITY_LABEL).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
          </SelectContent>
        </Select>
      </PageHeader>

      {entries?.length > 0 && filtered.length === 0 ? (
        <EmptyState icon={History} title="No matching entries" description="Adjust your search or filters." />
      ) : filtered.length === 0 ? <EmptyState icon={History} title="No audit entries yet" description="Tracked changes across the app will show up here." /> : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm" data-testid="audit-log-table">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-semibold">When</th>
                <th className="px-4 py-3 font-semibold">Actor</th>
                <th className="px-4 py-3 font-semibold">Action</th>
                <th className="px-4 py-3 font-semibold">Entity</th>
                <th className="px-4 py-3 font-semibold">Details</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id} data-testid={`audit-row-${e.id}`} className="border-b border-border last:border-0 hover:bg-muted/50">
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-muted-foreground">{relative(e.at)}</td>
                  <td className="px-4 py-3 font-medium">{e.user_name}</td>
                  <td className="px-4 py-3"><StatusBadge family={ACTION_COLOR[e.action] || "slate"} label={e.action.replace("_", " ")} /></td>
                  <td className="px-4 py-3 text-muted-foreground">{ENTITY_LABEL[e.entity_type] || e.entity_type} · <span className="text-xs">{e.entity_id}</span></td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {e.changes && Object.keys(e.changes).length > 0
                      ? Object.entries(e.changes).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`).join(", ")
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
